/**
 * `control-height` 32px, `bg-inset`, unit suffix inside the field, steppers on
 * hover (components.md §3).
 *
 * Numeric inputs are `.ud-num` and right-aligned, which is not decoration: the
 * ticket's price and size fields sit directly under the orderbook's right-
 * aligned price column, and a left-aligned size field breaks the one vertical
 * line of digits the layout is built around.
 *
 * `inputMode="decimal"` rather than `type="number"`: a number input silently
 * accepts `1e5`, rounds on some platforms, and gives no way to keep a trailing
 * zero — all three are unacceptable for a field whose value is parsed by
 * `lib/decimal` as an exact string.
 */

import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode
  /** `USDC`, `¢`, `shares` — rendered inside the field in `text-tertiary`. */
  unit?: string
  /** Renders the field in `danger` and shows the message below it. */
  error?: string
  numeric?: boolean
  onStep?: (delta: 1 | -1) => void
}

export function Input({
  label,
  unit,
  error,
  numeric = true,
  onStep,
  className,
  id,
  disabled,
  ...rest
}: InputProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  const errorId = `${inputId}-error`

  return (
    <div className="group flex flex-col gap-1">
      {label ? (
        <label htmlFor={inputId} className="text-caption text-text-secondary">
          {label}
        </label>
      ) : null}

      <div
        className={cx(
          'relative flex items-center rounded-md border bg-bg-inset',
          // **The border is outside the floor.** A 44px field with a 1px border
          // holds a 42px input, and the input is what a finger lands on — same
          // two pixels `<SegmentedControl>` lost. Below 768px the box grows by
          // the border so the *control* is 44px; above it, unchanged (Phase 10).
          'h-[calc(var(--ud-tap-min)+2px)] md:h-[var(--ud-control-height)]',
          'transition-[border-color,box-shadow] duration-[var(--ud-duration-instant)]',
          error ? 'border-danger-solid' : 'border-border-default focus-within:border-border-focus',
          'focus-within:shadow-focus',
          disabled && 'opacity-55',
          className,
        )}
      >
        <input
          {...rest}
          id={inputId}
          disabled={disabled}
          inputMode={numeric ? 'decimal' : rest.inputMode}
          autoComplete={rest.autoComplete ?? 'off'}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : rest['aria-describedby']}
          className={cx(
            // `h-full`, not just `w-full`: without it the input is its intrinsic
            // ~20px inside a 44px field, so on a phone the top and bottom
            // thirds of a control that looks pressable are not (Phase 10).
            'h-full w-full bg-transparent px-2 text-body text-text-primary outline-none placeholder:text-text-disabled',
            numeric && 'ud-num text-right',
          )}
        />

        {unit ? (
          <span className="pointer-events-none pr-2 text-body-sm text-text-tertiary select-none">
            {unit}
          </span>
        ) : null}

        {onStep && !disabled ? (
          // Hidden until hover or keyboard focus: a permanent pair of steppers on
          // every field is 64px of chrome in a 320px column.
          <span className="mr-1 hidden shrink-0 flex-col group-hover:flex group-focus-within:flex">
            {([1, -1] as const).map((delta) => (
              <button
                key={delta}
                type="button"
                tabIndex={-1}
                aria-label={delta === 1 ? 'increase' : 'decrease'}
                onClick={() => onStep(delta)}
                className="flex h-[calc(13*var(--ud-u))] w-4 items-center justify-center text-micro text-text-tertiary hover:text-text-primary"
              >
                {delta === 1 ? '+' : '−'}
              </button>
            ))}
          </span>
        ) : null}
      </div>

      {error ? (
        <span id={errorId} role="alert" className="text-caption text-danger-text">
          {error}
        </span>
      ) : null}
    </div>
  )
}
